import React, {useEffect, useMemo, useState} from 'react';
import {Radio} from 'antd';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import FormInput from '../../../../../common/FormInput';
import AdvanceSelect from '../../../../../common/AdvanceSelect';
import SharedTimepicker from '../../../../../common/SharedTimepicker';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import {t} from '../../../../../../services/i18n';
import {fetchApi} from '../../../../../../services/HTTP';
import {getCategoriesByTypes} from '../../../../../../redux/ScheduleSlice';
import {APPOINTMENT, APPOINTMENT_WAITLIST_SETTINGS, CATEGORY_SELECT} from '../../../../../../services/ConstHelper';
import {
    dbDateFormat,
    dbTimeFormat,
    getAllowedLocations,
    getLocationsForPermission,
    isMobile
} from '../../../../../../services/Helpers';
import orderBy from "lodash-es/orderBy";
import {Colors} from "../../../../../../styles/Colors";

const ANY_STAFF = 'any';
const MAX_TIME_RANGES = 3;
const MAX_DATE_DAYS = 30;

const AppointmentWaitlistAdd = (props) => {
    const {values, setFieldValue, errors, touched, validationSchema} = props;
    const dispatch = useDispatch();
    const services = useSelector(state => state.schedule.eventTypes.data);
    const categoryTypes = useSelector(state => state.schedule.categoryTypes);
    const box = useSelector(state => state.box.box);
    const user = useSelector(state => state.user.user);
    const selectedLocation = useSelector(state => state.userSettings.scheduleLocation);
    const [coachOptions, setCoachOptions] = useState([{value: ANY_STAFF, label: t('appointment-waitlist-any-team-member')}]);
    const today = useMemo(() => moment().startOf('day'), []);
    const maxDate = useMemo(() => moment().add(MAX_DATE_DAYS, 'days'), []);
    const clientLabel = values.user?.full_name || `${values.user?.first_name ?? ''} ${values.user?.last_name ?? ''}`.trim();
    const sessionTypeId = useMemo(() => categoryTypes?.find(type => type.name === APPOINTMENT)?.id, [categoryTypes]);
    const sessionTypes = useMemo(() => {
        if (!services?.appointment) return [];
        return services.appointment.filter(item => !!item.active).map(item => ({...item, value: item.id, label: item.name}));
    }, [services]);
    const locations = useMemo(() => {
        if (!box) return [];
        const base = getLocationsForPermission(box.locations_box, ['appointmentWaitlistAdd'], user);
        const pool = base.length ? base : getAllowedLocations(box.locations_box, user);
        return pool.reduce((acc, location) => [...acc, {...location, label: location.location, value: location.id}], []);
    }, [box, user]);
    const locationSelectDisabled = useMemo(() => !!user?.network_management && locations?.length === 1, [user, locations]);
    const boxBusinessHours = useMemo(() => box?.settings?.[APPOINTMENT_WAITLIST_SETTINGS]?.business_hours ?? null, [box]);

    useEffect(() => {
        if (!services) dispatch(getCategoriesByTypes(true));
    }, [services]);

    useEffect(() => {
        if (sessionTypes.length === 1 && !values.box_category_id) setFieldValue('box_category_id', sessionTypes[0].id);
    }, [sessionTypes]);

    useEffect(() => {
        if (locations?.length === 1) {
            setFieldValue('locations_box_id', locations[0].id);
        } else if (values.locations_box_id && locations?.length && !locations.some(location => location.id === values.locations_box_id)) {
            setFieldValue('locations_box_id', null);
        }
    }, [locations]);

    useEffect(() => {
        if (selectedLocation && !values.locations_box_id && locations.some(location => location.id === selectedLocation.id)) {
            setFieldValue('locations_box_id', selectedLocation.id);
        }
    }, [selectedLocation, values.locations_box_id, locations]);

    useEffect(() => {
        if (values.box_category_id && values.locations_box_id) fetchCoaches();
    }, [values.box_category_id, values.locations_box_id]);

    const fetchCoaches = async () => {
        try {
            const response = await fetchApi('availabilities/getAvailabilityCoaches', 'post', {
                box_categories_id: values.box_category_id,
                locations_box_id: values.locations_box_id,
            }, false, true);
            let mapped = (response ?? []).map(coach => ({
                ...coach,
                value: coach.id,
                label: coach.full_name || `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim(),
            }));
            mapped = orderBy(mapped, [item => item?.label?.toLowerCase()], ['asc'])
            setCoachOptions([{value: ANY_STAFF, label: t('appointment-waitlist-any-team-member')}, ...mapped]);
            if (values.coach_ub_id && !mapped.find(coach => coach.value === values.coach_ub_id)) {
                setFieldValue('coach_ub_id', null);
            }
        } catch (e) {
            setCoachOptions([{value: ANY_STAFF, label: t('appointment-waitlist-any-team-member')}]);
        }
    };

    const onDateModeChange = (mode) => {
        setFieldValue('dateMode', mode);
        if (mode === 'specific') {
            setFieldValue('to', values.from);
            return;
        }
        const rangeEnd = moment(values.from, dbDateFormat).add(7, 'days');
        setFieldValue('to', (rangeEnd.isAfter(maxDate) ? maxDate : rangeEnd).format(dbDateFormat));
    };

    const onSpecificDateChange = (date) => {
        if (!date) return;
        const formatted = date.format(dbDateFormat);
        setFieldValue('from', formatted);
        setFieldValue('to', formatted);
    };

    const onRangeDateChange = (dates) => {
        if (!dates?.[0] || !dates?.[1]) return;
        setFieldValue('from', dates[0].format(dbDateFormat));
        setFieldValue('to', dates[1].format(dbDateFormat));
    };

    const onTimeModeSelect = (mode) => {
        if (mode === 'any') {
            if (values.timeRange !== null) setFieldValue('timeRange', null);
        } else if (values.timeRange === null) {
            setFieldValue('timeRange', boxBusinessHours ?? [{from: '09:00:00', to: '14:00:00'}, {from: '16:00:00', to: '18:00:00'}]);
        }
    };

    const addTimeRange = () => {
        setFieldValue('timeRange', [...values.timeRange, boxBusinessHours ? boxBusinessHours[0] : {from: '09:00:00', to: '12:00:00'}]);
    };

    const removeTimeRange = (index) => {
        const next = [...values.timeRange];
        next.splice(index, 1);
        setFieldValue('timeRange', next);
    };

    const getRowTimeBounds = (index) => {
        const range = boxBusinessHours?.[index];
        if (range) return {min: moment(range.from, dbTimeFormat), max: moment(range.to, dbTimeFormat)};
        if (!boxBusinessHours?.length) return {};
        const froms = boxBusinessHours.map(item => item.from).sort();
        const tos = boxBusinessHours.map(item => item.to).sort();
        return {min: moment(froms[0], dbTimeFormat), max: moment(tos[tos.length - 1], dbTimeFormat)};
    };

    return (
        <VStack style={{gap: 0, width: '100%'}}>
            <FormInput width={'100%'} label={t('select-client')} required name={'users_id'} validationSchema={validationSchema} error={touched.users_id && errors.users_id ? errors.users_id : null}>
                <AdvanceSelect options={[{...values.user, label: clientLabel, value: values.user?.user_fk}]} initValue={values.user?.user_fk} isDisabled={true} hideCreateBtn/>
            </FormInput>
            <FormInput width={'100%'} label={t('appointment-waitlist-service')} infoTooltip={t('appointment-waitlist-service-tooltip')} required name={'box_category_id'} validationSchema={validationSchema} error={touched.box_category_id && errors.box_category_id ? errors.box_category_id : null}>
                <AdvanceSelect options={sessionTypes} initValue={values.box_category_id} type={CATEGORY_SELECT} categoryTypeId={sessionTypeId} hideCreateBtn onChange={(val) => setFieldValue('box_category_id', val?.id ?? val?.value ?? null)} isError={!!(touched.box_category_id && errors.box_category_id)}/>
            </FormInput>
            {(locations.length > 1 || locationSelectDisabled) &&
                <FormInput width={'100%'} label={t('location')} required name={'locations_box_id'} validationSchema={validationSchema} error={touched.locations_box_id && errors.locations_box_id ? errors.locations_box_id : null}>
                    <AdvanceSelect options={locations} initValue={values.locations_box_id} hideCreateBtn onChange={(val) => setFieldValue('locations_box_id', val?.id ?? val?.value ?? null)} isDisabled={locationSelectDisabled} isError={!!(touched.locations_box_id && errors.locations_box_id)}/>
                </FormInput>}
            <FormInput width={'100%'} label={t('appointment-waitlist-staff')} infoTooltip={t('appointment-waitlist-staff-tooltip')} name={'coach_ub_id'} validationSchema={validationSchema} error={touched.coach_ub_id && errors.coach_ub_id ? errors.coach_ub_id : null}>
                <AdvanceSelect options={coachOptions} initValue={values.coach_ub_id ?? ANY_STAFF} dontSort hideCreateBtn onChange={(val) => setFieldValue('coach_ub_id', !val?.value || val.value === ANY_STAFF ? null : val.value)} isError={!!(touched.coach_ub_id && errors.coach_ub_id)}/>
            </FormInput>
            <FormInput width={'100%'} label={t('appointment-waitlist-dates')} infoTooltip={t('appointment-waitlist-dates-tooltip')} required name={'from'} validationSchema={validationSchema} error={(touched.from && errors.from) || (touched.to && errors.to) ? (errors.from || errors.to) : null}>
                <VStack style={{gap: 12}}>
                    <Radio.Group name={'waitlist-date-mode'} value={values.dateMode} onChange={(e) => onDateModeChange(e.target.value)}>
                        <Radio value={'specific'}>{t('specific-date')}</Radio>
                        <Radio value={'range'}>{t('customDate')}</Radio>
                    </Radio.Group>
                    {values.dateMode === 'specific' ?
                        <SharedTimepicker type={'date'} value={moment(values.from, dbDateFormat)} onChange={onSpecificDateChange} min={today.format(dbDateFormat)} max={maxDate.format(dbDateFormat)} isError={!!(touched.from && errors.from)} inputStyle={[{width: isMobile ? '100%' : '10rem'}]}/>
                        :
                        <SharedTimepicker type={'date'} select={'range'} value={[moment(values.from, dbDateFormat), moment(values.to, dbDateFormat)]} onChange={onRangeDateChange} min={today.format(dbDateFormat)} max={maxDate.format(dbDateFormat)} isError={!!((touched.from && errors.from) || (touched.to && errors.to))} inputStyle={[{width: isMobile ? '100%' : '14rem'}]}/>
                    }
                </VStack>
            </FormInput>
            <FormInput width={'100%'} label={t('appointment-waitlist-times')} name={'timeRange'} validationSchema={validationSchema} error={typeof errors.timeRange === 'string' ? errors.timeRange : null} required>
                <VStack style={{gap: 12}}>
                    <Radio.Group name={'waitlist-time-mode'} value={values.timeRange === null ? 'any' : 'range'} onChange={(e) => onTimeModeSelect(e.target.value)}>
                        <Radio value={'any'}>{t('appointment-waitlist-any-time')}</Radio>
                        <Radio value={'range'}>{t('appointment-waitlist-time-range')}</Radio>
                    </Radio.Group>
                    {values.timeRange?.map((range, index) => {
                        const bounds = getRowTimeBounds(index);
                        return (
                            <HStack key={`time-range-${index}`} style={{gap: 10, alignItems: 'center'}}>
                                <SharedTimepicker type={'time'} value={moment(range.from, dbTimeFormat)} onChange={(time) => setFieldValue(`timeRange[${index}].from`, time.format(dbTimeFormat))} stepMinute={15} min={bounds.min} max={bounds.max} isError={!!(errors.timeRange?.[index]?.from)} inputStyle={[{width: '6rem'}]}/>
                                <span>-</span>
                                <SharedTimepicker type={'time'} value={moment(range.to, dbTimeFormat)} onChange={(time) => setFieldValue(`timeRange[${index}].to`, time.format(dbTimeFormat))} stepMinute={15} min={bounds.min} max={bounds.max} isError={!!(errors.timeRange?.[index]?.to)} inputStyle={[{width: '6rem'}]}/>
                                {index > 0 && <CustomButtonV2 type={'link'} iconName={'fa-trash-can'} onClick={() => removeTimeRange(index)} wrapperStyle={{width: 'min-content'}}/>}
                            </HStack>
                        );
                    })}
                    {values.timeRange?.length > 0 && values.timeRange.length < MAX_TIME_RANGES &&
                        <CustomButtonV2 type={'link'}
                                        text={t('appointment-waitlist-add-time-range')}
                                        onClick={addTimeRange}
                                        iconProps={{ iconHoverColor: Colors.newDesign.mainBlueHover, iconColor: Colors.newDesign.mainBlue }}
                                        applyHoverOnText={true}
                                        wrapperStyle={{width: 'min-content'}}/>}
                </VStack>
            </FormInput>
        </VStack>
    );
};

export default AppointmentWaitlistAdd;
